import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post, del } from '/api.js';
import { useQrSvg } from '/qr.js';
import { WatchQr } from '/watchqr.js';
import { CLOCKS, recall, remember } from '/clocks.js';
import { FORMATS } from '/formats.js';
import { Stepper } from '/stepper.js';

/**
 * The screen on the wall.
 *
 * A tablet enrolled at a venue is equipment, not a person: it holds one
 * capability, venue.match.create, it never plays, and it is never signed out.
 * It used to get the member app — an Elo rating it can never change, a
 * leaderboard, and a START A MATCH button that asked "who are you playing"
 * and was then refused by the server.
 *
 * So this is a different surface rather than the same one with the personal
 * parts hidden. One branch in app.js on `kind`, instead of a device check in
 * the gauge, the tab bar, the brand row, the record flow and settings, each of
 * which would have to keep remembering.
 *
 * Everything here comes from one request: /api/venues/{id} already carries the
 * roster, the circles and every match in progress, and the tablet may read its
 * own venue because enrolment granted it a capability there.
 */

const RACES = [3, 5, 7];
// Everybody starts with the same number, and three is what a pub game is
// usually played to. Any more and the game outlasts the round.
const LIVES = 3;

export function Kiosk({ authState, onGo }) {
  const [venue, setVenue] = useState(null);
  const [failed, setFailed] = useState(null);
  const [starting, setStarting] = useState(false);

  const venueId = authState.venue_id;

  const load = async () => {
    if (!venueId) { setFailed('This tablet is not enrolled at a venue.'); return; }
    try {
      setFailed(null);
      setVenue(await get(`/api/venues/${encodeURIComponent(venueId)}`));
    } catch (e) {
      setFailed(e.detail || e.message || 'no answer');
    }
  };

  useEffect(() => { load(); }, [venueId]);
  // A wall screen nobody is touching still has to be right: a match started on
  // a phone at the same table should appear here without anyone poking it.
  useEffect(() => {
    const t = setInterval(() => { if (!starting) load(); }, 10000);
    return () => clearInterval(t);
  }, [venueId, starting]);

  if (failed) {
    return html`
      <div class="screen kiosk" data-testid="kiosk">
        <p class="error" data-testid="kiosk-error">${failed}</p>
        <button onClick=${load}>Try again</button>
      </div>`;
  }
  if (!venue) return html`<p class="sub">Loading…</p>`;

  // Defaulted like the venue page, for the same reason: a wall tablet is the
  // screen most likely to be running yesterday's bundle against today's
  // server, and reading .length off a field that is not there kills the render.
  const live = venue.live || [];
  const games = venue.killer || [];
  const circles = venue.circles || [];
  const table = venue.table || {};
  // A table is in use if anything is on it — a match, a game of killer, or
  // two people knocking balls about who said so.
  const on = live.length + games.length + (table.claimed ? 1 : 0);

  if (starting) {
    return html`
      <${StartMatch} venue=${{ ...venue, circles }}
                     onOpened=${(id) => onGo('live', id)}
                     onOpenedKiller=${(id) => onGo('killer', id)}
                     onOpenedKnockout=${(id) => onGo('knockout', id)}
                     onDone=${() => { setStarting(false); load(); }} />`;
  }

  return html`
    <div class="screen kiosk" data-testid="kiosk">
      <div class="kioskhead">
        <h1 data-testid="kiosk-venue">${venue.name}</h1>
        <span class=${on ? 'status on' : 'status'}
              data-testid="kiosk-status">
          ${on ? 'In use' : 'Free'}
        </span>
      </div>

      <${LeaguesHere} leagues=${venue.leagues || []} onGo=${onGo} />

      ${games.length > 0 && html`
        <div class="stack" data-testid="kiosk-killer">
          <label>Killer on</label>
          <div class="tables">
            ${games.map((g) => html`
              <div class="card stack" key=${g.killer_id}
                   data-testid="kiosk-killer-table">
                <div class="rowbetween">
                  <span>${`Killer — ${g.players.length} playing`}</span>
                  <span class="mono" data-testid="kiosk-killer-turn">
                    ${g.turn_name}
                  </span>
                </div>
                <button data-testid=${`kiosk-killer-${g.killer_id}`}
                        onClick=${() => onGo('killer', g.killer_id)}>
                  Score it
                </button>
              </div>`)}
          </div>
        </div>`}

      ${on === 0
        ? html`<p class="empty" data-testid="kiosk-none">
                 No match on. Rack them up.
               </p>`
        : live.length === 0
          ? null
          : html`
          <div class="stack" data-testid="kiosk-live">
            <label>On now</label>
            <div class="tables">
            ${live.map((m) => html`
              <div class="card stack" key=${m.match_id} data-testid="kiosk-table">
                <div class="rowbetween">
                  <span>${m.players[0].name} v ${m.players[1].name}</span>
                  <span class="mono" data-testid="kiosk-score">
                    ${m.score[0]}–${m.score[1]}
                  </span>
                </div>
                <div class="row">
                  <button data-testid=${`kiosk-score-${m.match_id}`}
                          onClick=${() => onGo('live', m.match_id)}>
                    Score it
                  </button>
                  <${WatchQr} matchId=${m.match_id} />
                </div>
              </div>`)}
            </div>
          </div>`}

      ${table.can_start === false
        ? html`
          ${/* A game on cannot be shoved aside by starting another. The way
                to the table is the queue, so that is what is offered here
                instead of a button that would be refused. */''}
          <div class="onthetable" data-testid="kiosk-occupied">
            <span class="mono">There is a game on</span>
            <span class="sub">Join the queue and you will be called.</span>
          </div>`
        : html`
          <button class="felt" data-testid="kiosk-start"
                  onClick=${() => setStarting(true)}>Start a match</button>`}

      <${FreePlay} venue=${venue} onChanged=${load} />

      <${Queue} venue=${venue} onChanged=${load} />

      <div class="kioskgrid">
        <${TopThree} top=${venue.top || []} />
        <${CirclesHere} circles=${circles} />
        <${SignUp} />
      </div>
    </div>`;
}

/**
 * A way in to the league fixture being played at this table.
 *
 * `/api/venues/{id}` does not carry a fixture at all — no fixture_id, no
 * kick-off, no home/away — only which leagues this venue is IN, for the
 * "asking"/joined state already shown elsewhere on the venue's own page. So
 * this cannot jump straight to today's frame the way `kiosk-score-{id}`
 * does for a casual match; it opens the league itself
 * (`#/league/{league_id}`), whose own Match Hub is where the fixture is —
 * two taps for the referee rather than a route this endpoint has nothing to
 * answer for. Widening `/api/venues/{id}` to carry a fixture was left alone
 * rather than done here, since it is not this screen's endpoint to widen.
 *
 * Only leagues the venue has actually joined ("in") are offered — one it has
 * only asked to join has no fixtures to referee yet, and the venue's own
 * page is where that request is tracked.
 */
function LeaguesHere({ leagues, onGo }) {
  const joined = leagues.filter((l) => l.state === 'in');
  if (joined.length === 0) return null;
  return html`
    <div class="stack" data-testid="kiosk-leagues">
      <label>League play here</label>
      <div class="tables">
        ${joined.map((l) => html`
          <div class="card rowbetween" key=${l.league_id}
               data-testid=${`kiosk-league-${l.league_id}`}>
            <span>${l.name}</span>
            <button data-testid=${`kiosk-league-open-${l.league_id}`}
                    onClick=${() => onGo('league', l.league_id)}>
              Open
            </button>
          </div>`)}
      </div>
    </div>`;
}

/**
 * Two players, neither of them this tablet.
 *
 * The phone's flow asks who you are playing and sends the recorder as player
 * one. A tablet takes no part, so both sides are chosen — which the API has
 * always supported through `player_id` and no screen has ever sent.
 */
function StartMatch({ venue, onDone, onOpened, onOpenedKiller,
                     onOpenedKnockout }) {
  const [circleId, setCircleId] = useState(
    venue.circles.length === 1 ? venue.circles[0].circle_id : '');
  const [one, setOne] = useState('');
  const [two, setTwo] = useState('');
  const [format, setFormat] = useState('blackball');
  const [raceTo, setRaceTo] = useState(3);
  // Killer's own two answers. Kept beside the match ones rather than in place
  // of them, so switching game and switching back does not lose what was
  // already tapped in.
  const [inGame, setInGame] = useState([]);
  const [lives, setLives] = useState(LIVES);
  const [entrants, setEntrants] = useState([]);
  const [clocks, setClocks] = useState(
    () => ({ shot: recall(CLOCKS[0].key, 0), match: recall(CLOCKS[1].key, 0) }));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);

  // The CHOSEN circle's members, never the venue roster. A venue roster is who
  // drinks here; start_match refuses anybody who is not in the circle being
  // played in, so offering the roster offers people the match cannot be opened
  // against — and on a venue whose roster nobody has filled in, offers nobody
  // at all, which is how this was found.
  const circle = venue.circles.find((c) => c.circle_id === circleId);
  const roster = (circle && circle.people) || [];
  // Killer supersedes everything a match asks: no sides, no race, no clocks.
  // Several people round one table, a number of lives each, and that is it.
  const killer = format === 'killer';
  const knockout = format === 'knockout';
  const ready = circleId && (
    knockout ? entrants.length >= 2 && entrants.length <= 16
    : killer ? inGame.length >= 2
    : one && two && one !== two);

  const start = async () => {
    setBusy(true);
    try {
      setFailed(null);
      if (knockout) {
        const drawn = await post('/api/knockout', {
          circle_id: circleId, entrants, race_to: Number(raceTo),
          event_id: crypto.randomUUID(),
        });
        onOpenedKnockout(drawn.knockout_id);
        return;
      }
      if (killer) {
        const game = await post('/api/killer', {
          circle_id: circleId, players: inGame, lives: Number(lives),
          event_id: crypto.randomUUID(),
        });
        onOpenedKiller(game.killer_id);
        return;
      }
      const started = await post('/api/matches/start', {
        circle_id: circleId, player_id: one, opponent_id: two,
        game_format: format, race_to: Number(raceTo),
        shot_seconds: Number(clocks.shot),
        match_seconds: Number(clocks.match) * 60,
        event_id: crypto.randomUUID(),
      });
      // Straight to scoring: somebody is standing at the table waiting to
      // break. Coming back to the list would make them find the match they
      // just created before they could score a frame.
      onOpened(started.match_id);
    } catch (e) {
      setFailed(e.detail || e.message || 'could not start');
      setBusy(false);
    }
  };

  return html`
    <div class="screen kiosk" data-testid="kiosk-start-form">
      <div class="rowbetween">
        <h1>Start a match</h1>
        <button class="secondary" style="width:auto"
                data-testid="kiosk-cancel" onClick=${onDone}>Back</button>
      </div>

      ${failed && html`<p class="error" data-testid="kiosk-start-error">${failed}</p>`}

      ${venue.circles.length === 0
        ? html`<p class="empty" data-testid="kiosk-no-circles">
                 No circles at this venue yet. A manager makes one first.
               </p>`
        : html`
          ${venue.circles.length > 1 && html`
            <div class="stack" data-testid="kiosk-circle">
              <label>Which circle</label>
              <div class="seg">
                ${venue.circles.map((c) => html`
                  <button key=${c.circle_id}
                          aria-pressed=${c.circle_id === circleId}
                          data-testid=${`kiosk-circle-${c.circle_id}`}
                          onClick=${() => {
                            setCircleId(c.circle_id);
                            setOne(''); setTwo('');
                          }}>
                    ${c.name}
                  </button>`)}
              </div>
            </div>`}

          <div class="stack">
            <label>Game</label>
            <div class="seg">
              ${/* Knockout only where the venue says this caller may run
                    one — the tablet, whoever keeps the roster, whoever runs
                    the pub. Not offered at all otherwise, rather than offered
                    and refused. */''}
              ${FORMATS.filter((f) => !f.teamOnly || venue.may_knockout)
                .map((f) => html`
                <button key=${f.id} aria-pressed=${f.id === format}
                        data-testid=${`kiosk-format-${f.id}`}
                        onClick=${() => setFormat(f.id)}>${f.label}</button>`)}
            </div>
          </div>

          ${!circleId && html`
            <p class="empty" data-testid="kiosk-pick-circle-first">
              Choose a circle, then who is playing.
            </p>`}

          ${circleId && knockout && html`
            <${Field} people=${roster} chosen=${entrants}
                      onChange=${setEntrants} max=${16}
                      label="In the draw" testid="kiosk-draw"
                      prefix="kiosk-drawn" />
            <p class="sub" data-testid="kiosk-knockout-says">
              Straight knockout on blackball rules, up to sixteen. The draw is
              made when it starts; anybody left over gets a bye.
            </p>`}

          ${circleId && killer && html`
            <${Field} people=${roster} chosen=${inGame} onChange=${setInGame} />
            <div class="stack">
              <label>Lives each</label>
              <${Stepper} testid="kiosk-lives" label="lives" value=${lives}
                          min=${1} max=${9} onChange=${setLives} />
            </div>
            <p class="sub" data-testid="kiosk-killer-says">
              Everybody gets a shot in turn. Pot anything and you are safe;
              nothing down is a life. The black pays one back.
            </p>`}

          ${circleId && !killer && html`
            <${Picker} label="Playing" side="one" people=${roster}
                       chosen=${one} other=${two} onPick=${setOne} />
            <${Picker} label="Against" side="two" people=${roster}
                       chosen=${two} other=${one} onPick=${setTwo} />`}

          ${!killer && CLOCKS.map((clock) => html`
            <div class="stack" key=${clock.id}>
              <label>${clock.label}</label>
              <div class="seg" data-testid=${`kiosk-${clock.id}-clock`}>
                ${clock.options.map((v) => html`
                  <button key=${v} aria-pressed=${clocks[clock.id] === v}
                          data-testid=${`kiosk-${clock.id}-${v}`}
                          onClick=${() => {
                            remember(clock.key, v);
                            setClocks((c) => ({ ...c, [clock.id]: v }));
                          }}>
                    ${v === 0 ? 'Off' : `${v}${clock.unit}`}
                  </button>`)}
              </div>
            </div>`)}

          ${!killer && html`
          <div class="stack">
            <label>Race to</label>
            <div class="seg">
              ${RACES.map((n) => html`
                <button key=${n} aria-pressed=${n === raceTo}
                        data-testid=${`kiosk-race-${n}`}
                        onClick=${() => setRaceTo(n)}>${n}</button>`)}
            </div>
          </div>`}

          <button class="felt" disabled=${!ready || busy}
                  data-testid="kiosk-open" onClick=${start}>
            ${busy ? 'Starting…' : killer ? 'Open the game' : 'Open the match'}
          </button>`}
    </div>`;
}

/**
 * Who is in the killer game.
 *
 * A toggle each rather than two pickers: killer takes anywhere from two people
 * to the whole circle, and the order they are tapped in is not the order they
 * play in — that is drawn when the game opens, so tapping is just "in or out".
 */
function Field({ people, chosen, onChange, max = 99,
                 label = 'Who is playing', testid = 'kiosk-field',
                 prefix = 'kiosk-in' }) {
  const flip = (id) => onChange(chosen.includes(id)
    ? chosen.filter((p) => p !== id)
    : chosen.length >= max ? chosen : [...chosen, id]);
  return html`
    <div class="stack" data-testid=${testid}>
      <label>${`${label}${chosen.length ? ` — ${chosen.length}` : ''}`}</label>
      ${people.length === 0
        ? html`<p class="empty">Nobody in this circle yet.</p>`
        : html`
          <div class="seg">
            ${people.map((p) => html`
              <button key=${p.user_id} aria-pressed=${chosen.includes(p.user_id)}
                      data-testid=${`${prefix}-${p.user_id}`}
                      onClick=${() => flip(p.user_id)}>${p.name}</button>`)}
          </div>`}
      ${chosen.length === 1 && html`
        <p class="sub" data-testid="kiosk-need-two">
          It takes at least two.
        </p>`}
      ${chosen.length >= max && html`
        <p class="sub" data-testid="kiosk-field-full">
          ${`That is the most it takes — ${max}.`}
        </p>`}
    </div>`;
}

/** One side of the match. The person on the other side is not offered. */
function Picker({ label, side, people, chosen, other, onPick }) {
  return html`
    <div class="stack" data-testid=${`kiosk-pick-${side}`}>
      <label>${label}</label>
      ${people.length === 0
        ? html`<p class="empty">Nobody in this circle yet.</p>`
        : html`
          <div class="seg">
            ${people.filter((p) => p.user_id !== other).map((p) => html`
              <button key=${p.user_id} aria-pressed=${p.user_id === chosen}
                      data-testid=${`kiosk-${side}-${p.user_id}`}
                      onClick=${() => onPick(p.user_id)}>${p.name}</button>`)}
          </div>`}
    </div>`;
}


/** "frees itself in ..." — seconds while it is close, minutes while it is not. */
function howLong(left) {
  const n = left || 0;
  return n < 60 ? `frees itself in ${n}s`
    : `frees itself in ${Math.ceil(n / 60)} min`;
}

/**
 * The line for the table.
 *
 * The number is the point — somebody walking in wants to know whether it is
 * worth waiting, and "3 waiting" answers that from across the room. The names
 * are under it because the person who is up needs to see themselves.
 *
 * A guest joins with a placeholder name on purpose. "John Smith" reads as a
 * stand-in, and the QR code is a foot to the right of it.
 */
function Queue({ venue, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);
  const queue = venue.queue || { waiting: 0, entries: [], guest_names: [] };
  const entries = queue.entries || [];
  const at = `/api/venues/${encodeURIComponent(venue.venue_id)}/queue`;

  const act = async (fn) => {
    setBusy(true);
    setFailed(null);
    try { await fn(); await onChanged(); }
    catch (e) { setFailed(e.detail || e.message); }
    finally { setBusy(false); }
  };

  const called = entries.find((e) => e.state === 'called');

  return html`
    <div class="stack" data-testid="kiosk-queue">
      <div class="rowbetween">
        <label>Waiting for the table</label>
        <span class="mono waiting" data-testid="kiosk-waiting">
          ${`${queue.waiting} waiting`}
        </span>
      </div>

      ${/* Whoever has been called gets the whole width and a countdown. This
            is the screen they are walking towards. */''}
      ${called && html`
        <div class="uptonow" data-testid="kiosk-called">
          <div class="name">
            ${called.table ? `You are up — ${called.table}` : 'You are up'}
          </div>
          <div class="figure" data-testid="kiosk-called-name">
            ${called.name}
          </div>
          <button class="felt" disabled=${busy}
                  data-testid="kiosk-here"
                  onClick=${() => act(() => post(
                    `${at}/${encodeURIComponent(called.entry_id)}/here`, {}))}>
            ${`I am here — ${called.seconds_left}s`}
          </button>
        </div>`}

      ${entries.length === 0
        ? html`<p class="empty" data-testid="kiosk-queue-empty">
                 Nobody waiting. The table is yours.
               </p>`
        : html`
          <ol class="line" data-testid="kiosk-line">
            ${entries.map((e, i) => html`
              <li key=${e.entry_id} class=${`seat ${e.state}`}
                  data-testid=${`kiosk-queued-${e.entry_id}`}>
                <span class="mono">${i + 1}</span>
                <span class="who">${e.name}</span>
                <span class="mono tag">
                  ${e.state === 'here' ? 'at the table'
                    : e.state === 'called' ? 'called' : ''}
                </span>
                <button class="secondary" style="width:auto" disabled=${busy}
                        data-testid=${`kiosk-drop-${e.entry_id}`}
                        onClick=${() => act(() => del(
                          `${at}/${encodeURIComponent(e.entry_id)}`))}>
                  Remove
                </button>
              </li>`)}
          </ol>`}

      <${QueueQr} venueId=${venue.venue_id} />

      <div class="row" data-testid="kiosk-join">
        ${(queue.guest_names || []).map((name) => html`
          <button key=${name} class="secondary" disabled=${busy}
                  data-testid=${`kiosk-join-${name.split(' ')[0].toLowerCase()}`}
                  onClick=${() => act(() => post(at, {
                    guest: name, event_id: crypto.randomUUID(),
                  }))}>
            ${`Join as ${name}`}
          </button>`)}
      </div>
      ${failed && html`<p class="error" data-testid="kiosk-queue-error">
                         ${failed}
                       </p>`}
    </div>`;
}

/**
 * The code that puts you in the line as yourself.
 *
 * Joining at the tablet is only ever as a guest, and it has to be — this
 * screen cannot know who is standing at it. A phone knows exactly who it is,
 * so the code carries the pub and the phone carries the person. It also means
 * being CALLED works properly: a guest can only be shouted at, and somebody
 * who joined as themselves gets a push while they are at the bar.
 */
function QueueQr({ venueId }) {
  const [path, setPath] = useState(null);
  useEffect(() => {
    let live = true;
    let timer = null;
    const mint = () => {
      get(`/api/venues/${encodeURIComponent(venueId)}/queue-link`)
        .then((r) => {
          if (!live) return;
          setPath(r.path);
          // Ask for the next one at half its life. The code on the wall is
          // then never within half its own lifetime of lapsing, so nobody
          // scans one that dies between the camera and the tap. The server
          // says how long it has; this screen does not keep its own copy of
          // that number.
          const life = (r.expires_in || 600) * 1000;
          timer = setTimeout(mint, Math.max(30000, life / 2));
        })
        .catch(() => { if (live) setPath(null); });
    };
    mint();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [venueId]);
  const svg = useQrSvg(path ? `${location.origin}${path}` : '',
                       { cellSize: 4 });
  if (!path) return null;
  return html`
    <div class="queueqr" data-testid="kiosk-queue-qr">
      <div class="qr" dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <div>
        <div class="mono">Scan to join as yourself</div>
        <p class="sub">
          Your phone buzzes when the table is yours. Or tap below to go in the
          line as a guest.
        </p>
      </div>
    </div>`;
}

/** Who is worth beating here. Three, because a wall is not a leaderboard. */
function TopThree({ top }) {
  return html`
    <div class="stack card" data-testid="kiosk-top">
      <label>Best here</label>
      ${top.length === 0
        ? html`<p class="empty" data-testid="kiosk-no-top">
                 Nobody has played enough yet.
               </p>`
        : top.map((p, i) => html`
            <div class="rowbetween" key=${p.user_id}
                 data-testid=${`kiosk-top-${i}`}>
              <span>${`${i + 1}. ${p.name}`}</span>
              <span class="figure small">${p.elo}</span>
            </div>`)}
    </div>`;
}

/**
 * The circles that play here, by name only.
 *
 * Deliberately not who is in them. This screen is bolted to a wall in a public
 * room: a list of everybody who drinks here, readable by anybody who walks
 * past, is not something the pub should be publishing on the tablet's behalf.
 */
function CirclesHere({ circles }) {
  return html`
    <div class="stack card" data-testid="kiosk-circles">
      <label>Circles here</label>
      ${circles.length === 0
        ? html`<p class="empty">None yet.</p>`
        : circles.map((c) => html`
            <div key=${c.circle_id} data-testid="kiosk-circle-name">
              ${c.name}
            </div>`)}
    </div>`;
}

/**
 * Scan to get the app. The argument for it is the name above it.
 *
 * The app's own origin, not a sign-up link: registration is invite-only and
 * deliberately so, and a wall screen in a public room is exactly where an
 * open door would be walked through. This gets somebody to the landing page
 * that tells them they need a code — which is the truth.
 */
function SignUp() {
  const svg = useQrSvg(location.origin, { cellSize: 4 });
  return html`
    <div class="stack card" data-testid="kiosk-signup">
      <label>Get the app</label>
      <div class="qr" data-testid="kiosk-signup-qr"
           dangerouslySetInnerHTML=${{ __html: svg || '' }}></div>
      <p class="sub">Scan to put your own name on the board.</p>
    </div>`;
}


/**
 * Free play: the table is in use without a match on it.
 *
 * The app only ever knew about games it had been told about, so two people
 * knocking balls about read as "Free" on the wall and the queue called
 * somebody to a table with a game on it. One button fixes that, and the same
 * button frees it again.
 *
 * It runs out on its own after a couple of hours. A claim nobody releases
 * would hold the table all night and the queue would silently stop moving,
 * which is a worse failure than the one this is here to fix.
 */
function FreePlay({ venue, onChanged }) {
  const [busy, setBusy] = useState(false);
  const table = venue.table || {};
  const at = `/api/venues/${encodeURIComponent(venue.venue_id)}/table`;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged(); } finally { setBusy(false); }
  };

  const claims = table.claims || [];
  const tables = table.tables || 1;

  return html`
    <div class="stack">
      ${claims.map((claim, i) => html`
        <div class="claimed" key=${claim.claim_id}
             data-testid=${i === 0 ? 'kiosk-claimed' : 'kiosk-claimed-more'}>
          <span>
            <span class="mono">
              ${claim.table ? `${claim.table} — in use` : 'On the table'}
            </span>
            <span class="sub"
                  data-testid=${i === 0 ? 'kiosk-claim-left' : ''}>
              ${howLong(claim.seconds_left)}
            </span>
          </span>
          <button class="secondary" style="width:auto" disabled=${busy}
                  data-testid=${`kiosk-free-${claim.claim_id}`}
                  onClick=${() => act(() => del(
                    `${at}?claim_id=${encodeURIComponent(claim.claim_id)}`))}>
            Free it
          </button>
        </div>`)}

      ${/* Offered while there is a table left to claim, so a pub with two of
            them can have two groups on. */''}
      ${(table.free === undefined || table.free > 0) && html`
        <button class="secondary" disabled=${busy} data-testid="kiosk-claim"
                onClick=${() => act(() => post(at, {}))}>
          Just having a game
        </button>`}
    </div>`;
}
